import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Plus, ChevronRight, Search, RefreshCw, X, Trash2, Edit2, ExternalLink, Newspaper, BarChart3, DollarSign, Activity, Building, AlertCircle } from 'lucide-react';

const API_URL = window.location.origin;

const RealEstateInvestments = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1D');
  const [selectedMarket, setSelectedMarket] = useState('NASDAQ');
  const [activeTab, setActiveTab] = useState('Stocks');
  
  // Stock state
  const [watchlist, setWatchlist] = useState([]);
  const [stockQuotes, setStockQuotes] = useState({});
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockFundamentals, setStockFundamentals] = useState(null);
  const [stockNews, setStockNews] = useState([]);
  const [intradayData, setIntradayData] = useState([]);
  const [portfolioValue, setPortfolioValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);
  const [error, setError] = useState('');
  
  // Market indices state
  const [marketIndices, setMarketIndices] = useState([]);
  const [indicesLoading, setIndicesLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [indexChartData, setIndexChartData] = useState([]);
  const [indexChartLoading, setIndexChartLoading] = useState(false);

  const tabs = ['Stocks', 'Properties'];

  // Fetch watchlist on mount
  useEffect(() => {
    if (activeTab === 'Stocks') {
      fetchWatchlist();
      fetchApiStatus();
      fetchMarketIndices();
    }
  }, [activeTab]);

  // Fetch market indices
  const fetchMarketIndices = async () => {
    setIndicesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stocks/market-indices`);
      if (response.ok) {
        const data = await response.json();
        setMarketIndices(data.indices || []);
      }
    } catch (err) {
      console.error('Failed to fetch market indices:', err);
    } finally {
      setIndicesLoading(false);
    }
  };

  // Fetch chart data for market index
  const fetchIndexChartData = async (symbol) => {
    setIndexChartLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stocks/chart/${symbol}?timeframe=1M`);
      if (response.ok) {
        const data = await response.json();
        setIndexChartData(data.chart?.data_points || []);
      } else {
        // Generate simulated chart data if API fails
        const simulatedData = generateSimulatedChartData();
        setIndexChartData(simulatedData);
      }
    } catch (err) {
      console.error('Failed to fetch index chart data:', err);
      // Generate simulated chart data on error
      const simulatedData = generateSimulatedChartData();
      setIndexChartData(simulatedData);
    } finally {
      setIndexChartLoading(false);
    }
  };

  // Generate simulated chart data for display
  const generateSimulatedChartData = () => {
    const data = [];
    const now = new Date();
    let baseValue = selectedIndex?.price || 1000;
    for (let i = 30; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      // Add some random variation
      const variation = (Math.random() - 0.5) * (baseValue * 0.02);
      baseValue += variation;
      data.push({
        date: date.toISOString().split('T')[0],
        close: baseValue
      });
    }
    return data;
  };

  // Open index chart modal
  const openIndexChart = (index) => {
    setSelectedIndex(index);
    fetchIndexChartData(index.symbol);
  };

  // Generate SVG path for index chart
  const generateIndexChartPath = (data, width = 600, height = 250) => {
    if (!data || data.length === 0) return '';
    
    const prices = data.map(d => d.close || d.price || 0);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice || 1;
    
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.close || d.price || 0) - minPrice) / priceRange * height;
      return `${x},${y}`;
    });
    
    return `M ${points.join(' L ')}`;
  };

  // Fetch quotes for watchlist
  useEffect(() => {
    if (watchlist.length > 0 && activeTab === 'Stocks') {
      fetchQuotesForWatchlist();
    }
  }, [watchlist, activeTab]);

  const fetchApiStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stocks/status`);
      const data = await response.json();
      setApiStatus(data);
    } catch (err) {
      console.error('Failed to fetch API status:', err);
    }
  };

  const fetchWatchlist = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stocks/watchlist`);
      const data = await response.json();
      setWatchlist(data.watchlist || []);
    } catch (err) {
      console.error('Failed to fetch watchlist:', err);
    }
  };

  const fetchQuotesForWatchlist = async () => {
    setLoading(true);
    const newQuotes = {};
    
    for (const item of watchlist) {
      try {
        const response = await fetch(`${API_URL}/api/stocks/quote/${item.symbol}`);
        if (response.ok) {
          const data = await response.json();
          newQuotes[item.symbol] = data.quote;
        }
      } catch (err) {
        console.error(`Failed to fetch quote for ${item.symbol}:`, err);
      }
    }
    
    setStockQuotes(newQuotes);
    setLoading(false);
    
    // Calculate portfolio value
    fetchPortfolioValue();
  };

  const fetchPortfolioValue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stocks/portfolio/value`);
      const data = await response.json();
      setPortfolioValue(data.portfolio);
    } catch (err) {
      console.error('Failed to fetch portfolio value:', err);
    }
  };

  const fetchStockDetails = async (symbol) => {
    setSelectedStock(symbol);
    setStockFundamentals(null);
    setStockNews([]);
    setIntradayData([]);
    
    // Fetch fundamentals
    try {
      const fundResponse = await fetch(`${API_URL}/api/stocks/fundamentals/${symbol}`);
      if (fundResponse.ok) {
        const fundData = await fundResponse.json();
        setStockFundamentals(fundData.fundamentals);
      }
    } catch (err) {
      console.error('Failed to fetch fundamentals:', err);
    }
    
    // Fetch news
    try {
      const newsResponse = await fetch(`${API_URL}/api/stocks/news/${symbol}`);
      if (newsResponse.ok) {
        const newsData = await newsResponse.json();
        setStockNews(newsData.news?.articles || []);
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
    }
    
    // Fetch chart data for current timeframe
    fetchChartData(symbol, selectedTimeframe);
  };

  const fetchChartData = async (symbol, timeframe) => {
    if (!symbol) return;
    
    setIntradayData([]); // Clear current data while loading
    
    try {
      const chartResponse = await fetch(`${API_URL}/api/stocks/chart/${symbol}?timeframe=${timeframe}`);
      if (chartResponse.ok) {
        const chartDataResult = await chartResponse.json();
        setIntradayData(chartDataResult.chart?.data_points || []);
      }
    } catch (err) {
      console.error('Failed to fetch chart data:', err);
    }
  };

  // Fetch new chart data when timeframe changes
  useEffect(() => {
    if (selectedStock) {
      fetchChartData(selectedStock, selectedTimeframe);
    }
  }, [selectedTimeframe]);

  const searchStocks = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/stocks/search?query=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const addToWatchlist = async (symbol, name) => {
    try {
      const response = await fetch(`${API_URL}/api/stocks/watchlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, name })
      });
      
      if (response.ok) {
        fetchWatchlist();
        setShowAddModal(false);
        setSearchQuery('');
        setSearchResults([]);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to add stock');
      }
    } catch (err) {
      setError('Failed to add stock');
    }
  };

  const removeFromWatchlist = async (symbol) => {
    try {
      await fetch(`${API_URL}/api/stocks/watchlist/${symbol}`, {
        method: 'DELETE'
      });
      fetchWatchlist();
      if (selectedStock === symbol) {
        setSelectedStock(null);
        setStockFundamentals(null);
        setStockNews([]);
      }
    } catch (err) {
      console.error('Failed to remove:', err);
    }
  };

  const updateWatchlistItem = async (symbol, shares, avgCost) => {
    try {
      const params = new URLSearchParams();
      if (shares !== undefined) params.append('shares', shares);
      if (avgCost !== undefined) params.append('avg_cost', avgCost);
      
      await fetch(`${API_URL}/api/stocks/watchlist/${symbol}?${params.toString()}`, {
        method: 'PATCH'
      });
      fetchWatchlist();
      setShowEditModal(false);
      setEditingStock(null);
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    if (value >= 1000000000) {
      return `$${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  const myProperties = [
    { 
      symbol: 'PROP-1', 
      name: 'Marina Bay Complex', 
      value: 2450000, 
      change: 5.63, 
      color: 'bg-green-100', 
      chartColor: 'text-green-500',
      location: 'Downtown',
      bgGradient: 'from-green-400 to-green-600'
    },
    { 
      symbol: 'PROP-2', 
      name: 'Sunset Towers', 
      value: 1850000, 
      change: 8.24, 
      color: 'bg-purple-100', 
      chartColor: 'text-purple-500',
      location: 'West District',
      bgGradient: 'from-purple-400 to-purple-600'
    },
    { 
      symbol: 'PROP-3', 
      name: 'Heritage Plaza', 
      value: 3200000, 
      change: 12.45, 
      color: 'bg-yellow-100', 
      chartColor: 'text-yellow-500',
      location: 'Historic Center',
      bgGradient: 'from-yellow-400 to-yellow-600'
    },
    { 
      symbol: 'PROP-4', 
      name: 'Oakwood Residences', 
      value: 1650000, 
      change: 6.78, 
      color: 'bg-teal-100', 
      chartColor: 'text-teal-500',
      location: 'Suburban Area',
      bgGradient: 'from-teal-400 to-teal-600'
    },
    { 
      symbol: 'PROP-5', 
      name: 'Tech Hub Building', 
      value: 4100000, 
      change: 15.92, 
      color: 'bg-pink-100', 
      chartColor: 'text-pink-500',
      location: 'Innovation District',
      bgGradient: 'from-pink-400 to-pink-600'
    }
  ];

  const timeframes = ['1D', '5D', '1M', '6M', '1Y'];
  const portfolioTimeframes = ['1D', '5D', '1M', '6M', '1Y', '5Y', 'Max'];
  const markets = ['NASDAQ', 'NYSE', 'S&P 500', 'DOW'];

  // Generate SVG path from intraday data
  const generateChartPath = (data, width = 800, height = 400) => {
    if (!data || data.length === 0) return '';
    
    const prices = data.map(d => d.close);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const range = maxPrice - minPrice || 1;
    
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.close - minPrice) / range) * height * 0.9 - height * 0.05;
      return `${x},${y}`;
    });
    
    return `M${points.join(' L')}`;
  };

  // Render Stocks Tab Content
  const renderStocksTab = () => (
    <div className="space-y-6">
      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-green-100 text-sm mb-1">
            <DollarSign size={16} />
            Portfolio Value
          </div>
          <p className="text-2xl font-bold text-white">
            {portfolioValue ? formatCurrency(portfolioValue.total_value) : '$0.00'}
          </p>
          {portfolioValue && portfolioValue.total_gain_loss !== 0 && (
            <div className={`flex items-center gap-1 mt-1 text-sm ${portfolioValue.total_gain_loss >= 0 ? 'text-green-200' : 'text-red-200'}`}>
              {portfolioValue.total_gain_loss >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              <span>{portfolioValue.total_gain_loss >= 0 ? '+' : ''}{formatCurrency(portfolioValue.total_gain_loss)} ({portfolioValue.total_gain_loss_percent.toFixed(2)}%)</span>
            </div>
          )}
        </div>
        
        <div className="bg-black border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Activity size={16} />
            Total Cost
          </div>
          <p className="text-2xl font-bold text-white">
            {portfolioValue ? formatCurrency(portfolioValue.total_cost) : '$0.00'}
          </p>
        </div>
        
        <div className="bg-black border border-gray-700 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <BarChart3 size={16} />
            Positions
          </div>
          <p className="text-2xl font-bold text-white">{watchlist.length}</p>
        </div>
        
        <div className="bg-black border border-gray-700 rounded-xl p-5 flex items-center justify-center">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
            data-testid="add-stock-btn"
          >
            <Plus size={18} />
            Add Stock
          </button>
        </div>
      </div>

      {/* Market Indices Section */}
      <div className="bg-black border border-gray-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Activity size={20} className="text-purple-400" />
            Market Indices
          </h3>
          <button
            onClick={fetchMarketIndices}
            className="text-gray-400 hover:text-white p-1"
            data-testid="refresh-indices-btn"
          >
            <RefreshCw size={18} className={indicesLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {marketIndices.length > 0 ? (
            marketIndices.map((index) => (
              <div 
                key={index.symbol} 
                onClick={() => openIndexChart(index)}
                className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 hover:border-purple-500/50 transition-colors cursor-pointer hover:bg-gray-800/50"
                data-testid={`market-index-${index.symbol}`}
              >
                <div className="text-gray-400 text-xs mb-1">{index.full_name}</div>
                <div className="text-white text-xl font-bold mb-1">
                  {index.price ? index.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                </div>
                <div className={`flex items-center gap-1 text-sm ${index.change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {index.change_percent >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  <span>{index.change_percent >= 0 ? '+' : ''}{index.change_percent?.toFixed(2)}%</span>
                  <span className="text-gray-500 ml-1">({index.change >= 0 ? '+' : ''}{index.change?.toFixed(2)})</span>
                </div>
                {index.stale && <div className="text-yellow-500 text-xs mt-1">Cached data</div>}
              </div>
            ))
          ) : (
            // Loading/placeholder state
            ['S&P 500', 'Dow Jones', 'Nasdaq', 'Russell 2000', 'NYSE'].map((name) => (
              <div key={name} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 animate-pulse">
                <div className="text-gray-400 text-xs mb-1">{name}</div>
                <div className="h-7 bg-gray-800 rounded w-24 mb-1"></div>
                <div className="h-5 bg-gray-800 rounded w-16"></div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Watchlist */}
        <div className="lg:col-span-4">
          <div className="bg-black border border-gray-700 rounded-xl p-4 h-[520px] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Watchlist</h3>
              <button
                onClick={fetchQuotesForWatchlist}
                className="text-gray-400 hover:text-white p-1"
                data-testid="refresh-quotes-btn"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
            
            {watchlist.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center">
                <BarChart3 className="mx-auto text-gray-600 mb-2" size={32} />
                <p className="text-gray-500 text-sm">No stocks in watchlist</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-3 text-purple-400 hover:text-purple-300 text-sm"
                >
                  + Add your first stock
                </button>
              </div>
            ) : (
              <div className="flex-1 space-y-2 overflow-y-auto pr-1">
                {watchlist.map((item) => {
                  const quote = stockQuotes[item.symbol];
                  const isPositive = quote ? parseFloat(quote.change_percent) >= 0 : true;
                  
                  return (
                    <div
                      key={item.symbol}
                      onClick={() => fetchStockDetails(item.symbol)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedStock === item.symbol
                          ? 'bg-purple-600/20 border border-purple-500'
                          : 'bg-gray-900 hover:bg-gray-800 border border-transparent'
                      }`}
                      data-testid={`stock-item-${item.symbol}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-white">{item.symbol}</p>
                          <p className="text-xs text-gray-400 truncate max-w-[120px]">{item.name}</p>
                          {item.shares > 0 && (
                            <p className="text-xs text-purple-400">{item.shares} shares</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">
                            {quote ? `$${quote.price.toFixed(2)}` : '...'}
                          </p>
                          {quote && (
                            <div className={`flex items-center justify-end gap-1 text-xs ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                              {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                              <span>{isPositive ? '+' : ''}{parseFloat(quote.change_percent).toFixed(2)}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Quick actions */}
                      <div className="flex gap-2 mt-2 pt-2 border-t border-gray-800">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingStock(item); setShowEditModal(true); }}
                          className="text-xs text-gray-400 hover:text-purple-400"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFromWatchlist(item.symbol); }}
                          className="text-xs text-gray-400 hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Center Column - Chart & Details */}
        <div className="lg:col-span-5">
          <div className="bg-black border border-gray-700 rounded-xl p-4 h-[520px] flex flex-col">
            {selectedStock && stockQuotes[selectedStock] ? (
              <>
                {/* Stock Header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedStock}</h2>
                    <p className="text-gray-400">{stockFundamentals?.name || watchlist.find(w => w.symbol === selectedStock)?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">${stockQuotes[selectedStock]?.price?.toFixed(2)}</p>
                    <div className={`flex items-center justify-end gap-1 ${
                      parseFloat(stockQuotes[selectedStock]?.change_percent) >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {parseFloat(stockQuotes[selectedStock]?.change_percent) >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                      <span className="font-semibold">
                        {stockQuotes[selectedStock]?.change >= 0 ? '+' : ''}
                        ${stockQuotes[selectedStock]?.change?.toFixed(2)} ({parseFloat(stockQuotes[selectedStock]?.change_percent).toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Timeframe Selector */}
                <div className="flex gap-2 mb-4">
                  {timeframes.map((tf) => (
                    <button
                      key={tf}
                      onClick={() => setSelectedTimeframe(tf)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        selectedTimeframe === tf
                          ? 'bg-purple-600 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      {tf}
                    </button>
                  ))}
                </div>

                {/* Mini Chart */}
                <div className="flex-1 mb-4 relative min-h-[180px]">
                  {intradayData.length > 0 ? (
                    <svg className="w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="stockChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor={parseFloat(stockQuotes[selectedStock]?.change_percent) >= 0 ? '#10b981' : '#ef4444'} stopOpacity="0.3" />
                          <stop offset="100%" stopColor={parseFloat(stockQuotes[selectedStock]?.change_percent) >= 0 ? '#10b981' : '#ef4444'} stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      
                      {/* Chart line */}
                      <path
                        d={generateChartPath(intradayData, 800, 200)}
                        fill="none"
                        stroke={parseFloat(stockQuotes[selectedStock]?.change_percent) >= 0 ? '#10b981' : '#ef4444'}
                        strokeWidth="2"
                      />
                      
                      {/* Fill area */}
                      <path
                        d={`${generateChartPath(intradayData, 800, 200)} L800,200 L0,200 Z`}
                        fill="url(#stockChartGradient)"
                      />
                    </svg>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <RefreshCw size={20} className="mr-2 animate-spin opacity-50" />
                      <p>Loading chart...</p>
                    </div>
                  )}
                </div>

                {/* Key Stats Grid */}
                <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-800">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Open</p>
                    <p className="text-sm font-medium text-white">${stockQuotes[selectedStock]?.open?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">High</p>
                    <p className="text-sm font-medium text-white">${stockQuotes[selectedStock]?.high?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Low</p>
                    <p className="text-sm font-medium text-white">${stockQuotes[selectedStock]?.low?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Volume</p>
                    <p className="text-sm font-medium text-white">{formatNumber(stockQuotes[selectedStock]?.volume)}</p>
                  </div>
                </div>
              </>
            ) : selectedStock ? (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                <RefreshCw size={32} className="mb-3 opacity-50 animate-spin" />
                <p>Loading {selectedStock} data...</p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                <BarChart3 size={48} className="mb-3 opacity-50" />
                <p>Select a stock to view details</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Fundamentals */}
        <div className="lg:col-span-3">
          <div className="bg-black border border-gray-700 rounded-xl p-4 h-[520px] overflow-y-auto">
          {selectedStock && stockFundamentals ? (
            <>
              <h3 className="text-lg font-semibold text-white mb-3">Fundamentals</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Market Cap</span>
                  <span className="text-white font-medium">{formatCurrency(stockFundamentals.market_cap)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">P/E Ratio</span>
                  <span className="text-white font-medium">{stockFundamentals.pe_ratio?.toFixed(2) || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">EPS</span>
                  <span className="text-white font-medium">${stockFundamentals.eps?.toFixed(2) || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Dividend Yield</span>
                  <span className="text-white font-medium">{(stockFundamentals.dividend_yield * 100)?.toFixed(2) || '0'}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">52W High</span>
                  <span className="text-white font-medium">${stockFundamentals.week_52_high?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">52W Low</span>
                  <span className="text-white font-medium">${stockFundamentals.week_52_low?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Beta</span>
                  <span className="text-white font-medium">{stockFundamentals.beta?.toFixed(2) || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Target Price</span>
                  <span className="text-purple-400 font-medium">${stockFundamentals.analyst_target_price?.toFixed(2) || 'N/A'}</span>
                </div>
              </div>
              
              {/* 52 Week Range Bar */}
              <div className="mt-4 pt-3 border-t border-gray-800">
                <p className="text-xs text-gray-500 mb-2">52 Week Range</p>
                <div className="relative h-2 bg-gray-800 rounded-full">
                  <div 
                    className="absolute h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                    style={{ 
                      width: `${Math.min(100, Math.max(0, ((stockQuotes[selectedStock]?.price - stockFundamentals.week_52_low) / (stockFundamentals.week_52_high - stockFundamentals.week_52_low)) * 100))}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>${stockFundamentals.week_52_low?.toFixed(0)}</span>
                  <span>${stockFundamentals.week_52_high?.toFixed(0)}</span>
                </div>
              </div>
            </>
          ) : selectedStock ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <RefreshCw size={24} className="mb-2 opacity-50 animate-spin" />
              <p className="text-sm">Loading fundamentals...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <BarChart3 size={32} className="mb-2 opacity-30" />
              <p className="text-sm">Select a stock to view fundamentals</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Full-width News Section at Bottom */}
      {selectedStock && (
        <div className="mt-6 bg-black border border-gray-700 rounded-xl p-6" data-testid="stock-news-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600/20 rounded-lg">
                <Newspaper size={24} className="text-purple-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Latest News & Articles</h3>
                <p className="text-sm text-gray-400">
                  {stockNews.length > 0 
                    ? `${stockNews.length} articles about ${selectedStock} • ${stockFundamentals?.name || watchlist.find(w => w.symbol === selectedStock)?.name || selectedStock}`
                    : `No recent news for ${selectedStock}`
                  }
                </p>
              </div>
            </div>
            {stockNews.length > 0 && (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  stockNews.filter(n => n.sentiment === 'Bullish').length > stockNews.filter(n => n.sentiment === 'Bearish').length
                    ? 'bg-green-500/20 text-green-400'
                    : stockNews.filter(n => n.sentiment === 'Bearish').length > stockNews.filter(n => n.sentiment === 'Bullish').length
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  Overall: {
                    stockNews.filter(n => n.sentiment === 'Bullish').length > stockNews.filter(n => n.sentiment === 'Bearish').length
                      ? '📈 Bullish'
                      : stockNews.filter(n => n.sentiment === 'Bearish').length > stockNews.filter(n => n.sentiment === 'Bullish').length
                      ? '📉 Bearish'
                      : '➡️ Neutral'
                  }
                </span>
              </div>
            )}
          </div>
          
          {stockNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stockNews.map((article, idx) => (
                <a
                  key={idx}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gray-900/50 border border-gray-800 rounded-xl p-4 hover:border-purple-500/50 hover:bg-gray-900 transition-all duration-200"
                  data-testid={`news-article-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className={`shrink-0 px-2 py-1 rounded text-xs font-medium ${
                      article.sentiment === 'Bullish' ? 'bg-green-500/20 text-green-400' :
                      article.sentiment === 'Bearish' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {article.sentiment === 'Bullish' ? '📈 Bullish' : 
                       article.sentiment === 'Bearish' ? '📉 Bearish' : '➡️ Neutral'}
                    </span>
                    <ExternalLink size={14} className="text-gray-600 group-hover:text-purple-400 transition-colors" />
                  </div>
                  
                  <h4 className="text-white font-semibold mb-2 line-clamp-2 group-hover:text-purple-300 transition-colors">
                    {article.title}
                  </h4>
                  
                  <p className="text-gray-400 text-sm line-clamp-3 mb-3">
                    {article.summary}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 font-medium">{article.source}</span>
                    {article.sentiment_score && (
                      <span className={`${
                        article.sentiment_score > 0.2 ? 'text-green-400' :
                        article.sentiment_score < -0.2 ? 'text-red-400' :
                        'text-gray-400'
                      }`}>
                        Score: {(article.sentiment_score * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Newspaper size={48} className="mb-3 opacity-30" />
              <p className="text-lg">No recent news articles found</p>
              <p className="text-sm mt-1">News data may be unavailable due to API limits</p>
            </div>
          )}
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Add Stock to Watchlist</h3>
              <button onClick={() => { setShowAddModal(false); setSearchQuery(''); setSearchResults([]); }} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); searchStocks(e.target.value); }}
                  placeholder="Search by symbol or company name..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  data-testid="stock-search-input"
                />
              </div>
              
              {searchResults.length > 0 && (
                <div className="mt-3 max-h-[300px] overflow-y-auto space-y-2">
                  {searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => addToWatchlist(result.symbol, result.name)}
                      className="w-full p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-left transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-white">{result.symbol}</p>
                          <p className="text-sm text-gray-400 truncate">{result.name}</p>
                        </div>
                        <span className="text-xs text-gray-500">{result.region}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {searchQuery && searchResults.length === 0 && (
                <p className="mt-4 text-center text-gray-500 text-sm">No results found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Stock Modal */}
      {showEditModal && editingStock && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Edit {editingStock.symbol}</h3>
              <button onClick={() => { setShowEditModal(false); setEditingStock(null); }} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              updateWatchlistItem(
                editingStock.symbol,
                parseFloat(formData.get('shares')) || 0,
                parseFloat(formData.get('avgCost')) || 0
              );
            }} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Shares Owned</label>
                <input
                  type="number"
                  name="shares"
                  step="0.001"
                  defaultValue={editingStock.shares || 0}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Average Cost Per Share</label>
                <input
                  type="number"
                  name="avgCost"
                  step="0.01"
                  defaultValue={editingStock.avg_cost || 0}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingStock(null); }}
                  className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // Render Properties Tab (Original Content)
  const renderPropertiesTab = () => (
    <>
      {/* My Properties Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">My Properties</h2>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
          {myProperties.map((property, index) => (
            <div
              key={index}
              className={`min-w-[220px] rounded-xl p-4 bg-gradient-to-br ${property.bgGradient} relative overflow-hidden cursor-pointer hover:scale-105 transition-transform`}
            >
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-medium opacity-90">{property.symbol}</p>
                    <p className="text-xs opacity-75">{property.name}</p>
                  </div>
                </div>
                <p className="text-2xl font-bold mb-1">{formatCurrency(property.value)}</p>
                <div className="flex items-center gap-1 text-sm">
                  <TrendingUp className="w-4 h-4" />
                  <span>+{property.change}%</span>
                </div>
                <div className="mt-3 h-12 flex items-end">
                  <svg className="w-full h-full" viewBox="0 0 100 40" preserveAspectRatio="none">
                    <polyline
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      points="0,35 20,30 40,25 60,20 80,15 100,10"
                      className="opacity-80"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
          <div className="min-w-[80px] flex items-center justify-center">
            <button className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center hover:bg-purple-700 transition-colors">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-3 space-y-6">
          {/* Balance Card */}
          <div className="bg-black border border-gray-400 rounded-lg p-6 hover:shadow-lg hover:shadow-purple-500/20 transition-shadow">
            <p className="text-sm text-gray-400 mb-2">Balance</p>
            <p className="text-3xl font-bold text-purple-400 mb-2">$14,032.56</p>
            <div className="flex items-center gap-1 text-green-500 text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>+5.63%</span>
            </div>
          </div>

          {/* Invested Card */}
          <div className="bg-black border border-gray-400 rounded-lg p-6 hover:shadow-lg hover:shadow-purple-500/20 transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-400">Invested</p>
              <button className="text-purple-400 hover:text-purple-300">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <p className="text-3xl font-bold">$7,532.21</p>
          </div>

          {/* Top Property Card */}
          <div className="bg-black border border-gray-400 rounded-lg p-6 hover:shadow-lg hover:shadow-purple-500/20 transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-400">Top Property</p>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="font-semibold">Heritage Plaza</p>
                <p className="text-xs text-gray-400">PROP-3</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Invested</span>
                  <span className="font-medium">$2,850,000</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Current Value</span>
                  <span className="font-medium">$3,200,000</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Return</span>
                  <div className="flex items-center gap-1 text-green-500">
                    <TrendingUp className="w-3 h-3" />
                    <span className="font-medium">+12.45%</span>
                  </div>
                </div>
              </div>

              {/* Mini Chart */}
              <div className="h-16 mt-4">
                <svg className="w-full h-full" viewBox="0 0 200 50" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,40 L50,35 L100,25 L150,20 L200,10"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2"
                  />
                  <path
                    d="M0,40 L50,35 L100,25 L150,20 L200,10 L200,50 L0,50 Z"
                    fill="url(#chartGradient)"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Center Column - Large Chart */}
        <div className="lg:col-span-6">
          <div className="bg-black border border-gray-400 rounded-lg p-6 hover:shadow-lg hover:shadow-purple-500/20 transition-shadow">
            {/* Market Selector */}
            <div className="flex gap-4 mb-6 border-b border-gray-800">
              {markets.map((market) => (
                <button
                  key={market}
                  onClick={() => setSelectedMarket(market)}
                  className={`pb-2 text-sm font-medium transition-colors ${
                    selectedMarket === market
                      ? 'text-white border-b-2 border-purple-500'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {market}
                </button>
              ))}
            </div>

            {/* Timeframe Selector */}
            <div className="flex gap-4 mb-6">
              {timeframes.map((tf) => (
                <button
                  key={tf}
                  onClick={() => setSelectedTimeframe(tf)}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    selectedTimeframe === tf
                      ? 'bg-purple-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  {tf}
                </button>
              ))}
            </div>

            {/* Main Chart */}
            <div className="relative h-[400px] mb-6">
              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500 pr-2">
                <span>11,700</span>
                <span>11,650</span>
                <span>11,600</span>
                <span>11,550</span>
              </div>

              {/* Chart area */}
              <div className="ml-12 h-full relative">
                <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="mainChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  
                  {/* Grid lines */}
                  <line x1="0" y1="100" x2="800" y2="100" stroke="#1f2937" strokeWidth="1" />
                  <line x1="0" y1="200" x2="800" y2="200" stroke="#1f2937" strokeWidth="1" />
                  <line x1="0" y1="300" x2="800" y2="300" stroke="#1f2937" strokeWidth="1" />

                  {/* Chart line */}
                  <path
                    d="M0,350 L100,340 L200,320 L300,300 L400,280 L500,250 L600,200 L700,150 L800,100"
                    fill="none"
                    stroke="#a855f7"
                    strokeWidth="3"
                  />
                  
                  {/* Fill area */}
                  <path
                    d="M0,350 L100,340 L200,320 L300,300 L400,280 L500,250 L600,200 L700,150 L800,100 L800,400 L0,400 Z"
                    fill="url(#mainChartGradient)"
                  />

                  {/* Data point */}
                  <circle cx="500" cy="250" r="6" fill="#a855f7" />
                </svg>

                {/* Tooltip */}
                <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs">
                  <p className="text-gray-400">Jan 30, 01:12:16 AM</p>
                  <p className="text-xl font-bold text-purple-400">$14,032.56</p>
                </div>
              </div>

              {/* X-axis labels */}
              <div className="ml-12 flex justify-between text-xs text-gray-500 mt-2">
                <span>10 am</span>
                <span>11 am</span>
                <span>12 pm</span>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 pt-6 border-t border-gray-800">
              <div>
                <p className="text-xs text-gray-400 mb-1">High</p>
                <p className="text-sm font-semibold">11,691.80</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Low</p>
                <p className="text-sm font-semibold">11,470.47</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Open</p>
                <p className="text-sm font-semibold">11,690.11</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Prev Close (Avg 28 Days)</p>
                <p className="text-sm font-semibold">11,512.41</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-3 space-y-6">
          {/* Snapshot Card */}
          <div className="bg-black border border-gray-400 rounded-lg p-6 hover:shadow-lg hover:shadow-purple-500/20 transition-shadow">
            <h3 className="text-lg font-semibold mb-4">Snapshot</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Prev Close</span>
                  <span className="font-medium">12,051.48</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Open</span>
                  <span className="font-medium">12,000.21</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Day Low/High</span>
                </div>
                <div className="relative h-2 bg-gray-800 rounded-full mt-2">
                  <div 
                    className="absolute h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                    style={{ width: '65%' }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>11,099.87</span>
                  <span>12,168.60</span>
                  <span>12,248.16</span>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">52 Week Low/High</span>
                </div>
                <div className="relative h-2 bg-gray-800 rounded-full mt-2">
                  <div 
                    className="absolute h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                    style={{ width: '75%' }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>10,440.64</span>
                  <span>12,168.60</span>
                  <span>15,265.92</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4">Investments</h1>
        
        {/* Tabs */}
        <div className="flex gap-6 border-b border-gray-800">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab
                  ? 'text-white border-b-2 border-white'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
              data-testid={`tab-${tab.toLowerCase()}`}
            >
              {tab === 'Stocks' && <BarChart3 size={16} />}
              {tab === 'Properties' && <Building size={16} />}
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'Stocks' && renderStocksTab()}
      {activeTab === 'Properties' && renderPropertiesTab()}

      {/* Market Index Chart Modal */}
      {selectedIndex && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedIndex(null)}
          data-testid="index-chart-modal"
        >
          <div 
            className="bg-gray-900 rounded-2xl w-full max-w-2xl border border-gray-700 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">{selectedIndex.full_name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-2xl font-bold text-white">
                    {selectedIndex.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className={`flex items-center gap-1 text-sm ${selectedIndex.change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedIndex.change_percent >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    {selectedIndex.change_percent >= 0 ? '+' : ''}{selectedIndex.change_percent?.toFixed(2)}%
                    <span className="text-gray-400 ml-1">({selectedIndex.change >= 0 ? '+' : ''}{selectedIndex.change?.toFixed(2)})</span>
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedIndex(null)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Chart Area */}
            <div className="p-5">
              {indexChartLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <RefreshCw size={32} className="text-purple-400 animate-spin" />
                </div>
              ) : (
                <div className="relative h-64 bg-gray-800/50 rounded-xl p-4">
                  <svg width="100%" height="100%" viewBox="0 0 600 250" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="indexChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={selectedIndex.change_percent >= 0 ? "#22c55e" : "#ef4444"} stopOpacity="0.3" />
                        <stop offset="100%" stopColor={selectedIndex.change_percent >= 0 ? "#22c55e" : "#ef4444"} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {indexChartData.length > 0 && (
                      <>
                        {/* Area fill */}
                        <path
                          d={`${generateIndexChartPath(indexChartData, 600, 250)} L 600,250 L 0,250 Z`}
                          fill="url(#indexChartGradient)"
                        />
                        {/* Line */}
                        <path
                          d={generateIndexChartPath(indexChartData, 600, 250)}
                          fill="none"
                          stroke={selectedIndex.change_percent >= 0 ? "#22c55e" : "#ef4444"}
                          strokeWidth="2"
                        />
                      </>
                    )}
                  </svg>
                  
                  {/* Chart Labels */}
                  <div className="absolute bottom-2 left-4 right-4 flex justify-between text-xs text-gray-500">
                    <span>30 days ago</span>
                    <span>Today</span>
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4 mt-4">
                <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="text-gray-400 text-xs mb-1">Previous Close</div>
                  <div className="text-white font-semibold">
                    {selectedIndex.previous_close?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="text-gray-400 text-xs mb-1">Day Change</div>
                  <div className={`font-semibold ${selectedIndex.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedIndex.change >= 0 ? '+' : ''}{selectedIndex.change?.toFixed(2)}
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="text-gray-400 text-xs mb-1">% Change</div>
                  <div className={`font-semibold ${selectedIndex.change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {selectedIndex.change_percent >= 0 ? '+' : ''}{selectedIndex.change_percent?.toFixed(2)}%
                  </div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="text-gray-400 text-xs mb-1">Symbol</div>
                  <div className="text-white font-semibold">{selectedIndex.symbol}</div>
                </div>
              </div>

              {/* Note if not live data */}
              {!selectedIndex.live && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-400 text-sm">
                    <AlertCircle size={16} />
                    <span>Market data is approximated (API rate limit reached). Refreshes automatically when available.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateInvestments;
